import React from "react";
import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import logo from "logo.png";
import { Navbar, Nav, NavDropdown, Badge } from "react-bootstrap";
import { logout, selectAgency, fetchFunders, cleanLifeSettlementSearchData } from "actions";
import { activeAgencySelector, agenciesSelector, loginInfoSelector } from "reducers";
import { mainAgencyId, URLS } from "../constants";
import { redirectTo } from "../utils";
import { isAdmin } from "permissions";
import advisorhq from "../apis/advisorhq";
import * as notifications from "notifications";
import { getServerHost, getWebSocketProtocol } from "../actions/life_settlement/utils";
import { fetchNotifications } from "../actions/life_settlement/notifications";

const NavbarLink = ({ url, title, ...props }) => {
  const location = useLocation();
  const split = location.pathname.split("/");
  let isActive = false;
  if (split.length > 1) {
    const firstPart = split[1];
    if (url.startsWith("/riskx")) {
      isActive = firstPart.includes("riskx") || firstPart.includes("life-expectancies");
    } else {
      isActive = firstPart.indexOf(url.replaceAll("/", "")) !== -1;
    }
  }
  let activeClass = isActive ? "navbar-active" : "";
  return (
    <Link className={"nav-link " + activeClass} to={url} props={props}>
      {title}
    </Link>
  );
};

// example taken from https://react-bootstrap.netlify.com/components/navbar/#navbars
const AHQNavbar = ({ activeAgency, agencies, email, cleanLifeSettlementSearchData, roles, ...props }) => {
  const onAgencyChange = async agency => {
    const response = await advisorhq.patch("api/select_agency/", {
      active_agency: agency.id,
    });
    props.selectAgency({
      active_agency: response.data.active_agency,
      active_agency_roles: response.data.active_agency_roles,
    });
  };

  const displayAgencies = () => {
    let displayAgencyName = activeAgency?.name || "Select An Agency";
    let activeAgencyRole = roles.length > 0 ? roles[0] : "";

    if (agencies.length === 1) {
      return (
        <div style={{ marginTop: 5 }}>
          {displayAgencyName} ({activeAgencyRole})
        </div>
      );
    }
    return (
      <NavDropdown title={`${displayAgencyName} (${activeAgencyRole})`} id="agency-dropdown">
        {agencies.map(agency => (
          <NavDropdown.Item key={agency.id} onClick={() => onAgencyChange(agency)}>
            {agency.name} ({agency.role})
          </NavDropdown.Item>
        ))}
      </NavDropdown>
    );
  };

  const [nTasks, setNTasks] = React.useState(0);
  React.useEffect(async () => {
    if (props.userId) {
      let past_due_task_response = await advisorhq.get(`/life_settlement/api/past_due_task_count/`);
      let past_due_task_count = past_due_task_response.data.past_due_tasks;
      setNTasks(past_due_task_count);
    }
  }, [props.userId]);

  let myTasksTitle = "My Tasks";
  if (nTasks) {
    myTasksTitle = `My Tasks (${nTasks})`;
  }

  const [pendingNotificationCount, setPendingNotificationCount] = React.useState();
  const [last5Notifications, setLast5Notifications] = React.useState([]);
  let myNotificationsTitle = "Notifications";
  if (pendingNotificationCount) {
    myNotificationsTitle = `Notifications (${pendingNotificationCount})`;
  }

  React.useEffect(() => {
    let server_host = getServerHost();
    let protocol = getWebSocketProtocol();
    let url = `${protocol}://${server_host}socket-server/notifications/${props.userId}/`;
    const ws = new WebSocket(url);

    // Listen for the connection event
    ws.onopen = () => {
      // Send a request for update
      const requestTaskUpdate = {
        type: "request_notifications",
        user_id: props.userId,
      };
      ws.send(JSON.stringify(requestTaskUpdate));
    };

    ws.onmessage = event => {
      const data = JSON.parse(event.data);
      console.log("websocket onmessage", data);
      if (data.past_due_tasks !== undefined) {
        setNTasks(data.past_due_tasks);
      }
      if (data.notifications !== undefined) {
        setPendingNotificationCount(data.notifications.pending_count);
        setLast5Notifications(data.notifications.last_5);
      }
      if (data.updated_notification?.id) {
        let n = data.updated_notification;
        let nCaseId = n.case_submission_id || "";
        if (n.status === 2) {
          const message = `${n.type} ${nCaseId} completed`;
          notifications.success(message);
        } else if (n.status === 3) {
          const message = `${n.type} ${nCaseId} failed`;
          notifications.error(message);
        }
      }
      props.fetchNotifications();
    };
  }, []);

  const [show, setShow] = React.useState(false);
  const statusMap = {
    1: { icon: "🔄️", label: "started" },
    2: { icon: "✅", label: "finished" },
    3: { icon: "❌", label: "failed" },
  };

  function renderNavbarNotifications() {
    const items = last5Notifications.map((notification, index) => {
      const timestamp = new Date(notification.last_updated);
      const now = new Date();
      const minutesAgo = Math.floor((now - timestamp) / 60000);

      let timePassed;
      if (minutesAgo === 0) {
        timePassed = "just now";
      } else if (minutesAgo >= 1440) {
        const daysAgo = Math.floor(minutesAgo / 1440);
        timePassed = `${daysAgo}d ago`;
      } else if (minutesAgo >= 120) {
        const hoursAgo = Math.floor(minutesAgo / 60);
        timePassed = `${hoursAgo}h ago`;
      } else {
        timePassed = `${minutesAgo}m ago`;
      }
      const status = statusMap[notification.status] || { icon: "❓", label: "Unknown" };
      const case_id = notification.case_id;
      let link = `/cases/${case_id}/`;
      if (notification.case_document_id) {
        link += `files/${notification.case_document_id}/`;
      }
      return (
        <NavDropdown.Item
          key={index}
          href={link}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            redirectTo(link);
          }}
        >
          {status.icon} {notification.type} {notification.case_submission_id} {status.label} {timePassed}
        </NavDropdown.Item>
      );
    });

    items.push(
      <NavDropdown.Item
        key="view-all"
        href={URLS.NOTIFICATIONS}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          redirectTo(URLS.NOTIFICATIONS);
        }}
        className="text-center font-semibold"
      >
        View All
      </NavDropdown.Item>,
    );

    return items;
  }

  const location = useLocation();
  const isInNotifications = location.pathname.includes("notifications");

  return (
    <Navbar
      style={{ position: "fixed", top: 0, width: "100%", zIndex: 99, height: "4rem" }}
      expand="lg"
      className="shadowed"
    >
      <Navbar.Brand href={URLS.TASKS}>
        <img src={logo} alt="LifeRoc Analytics Logo" width="114" height="33" />
      </Navbar.Brand>

      <Navbar.Toggle aria-controls="basic-navbar-nav" />

      {props.isSignedIn && (
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="mr-auto">
            <NavbarLink title={myTasksTitle} url={URLS.TASKS} />
            <NavDropdown
              title={myNotificationsTitle}
              className={isInNotifications ? "navbar-active" : ""}
              id="notification-dropdown"
              url={URLS.NOTIFICATIONS}
              show={show}
              onMouseEnter={() => setShow(true)}
              onMouseLeave={() => setShow(false)}
              onClick={() => redirectTo(URLS.NOTIFICATIONS)}
            >
              {last5Notifications?.length > 0 ? (
                renderNavbarNotifications()
              ) : (
                <NavDropdown.Item disabled>No notifications</NavDropdown.Item>
              )}
            </NavDropdown>
            <NavbarLink title={"Accounts"} url={URLS.ACCOUNTS} />
            <NavbarLink title={"Carriers"} url={URLS.CARRIERS} />
            <NavbarLink title={"Cases"} url={URLS.CASES} onClick={() => cleanLifeSettlementSearchData()} />
            {props.hasAdminPermission && <NavbarLink title={"Counter Parties"} url={URLS.FUNDERS} />}
            {activeAgency?.id === mainAgencyId && <NavbarLink title={"Leads"} url={URLS.LEADS} />}
            <NavbarLink title={"Opportunities"} url={URLS.OPPORTUNITIES} />
            {props.hasAdminPermission && <NavbarLink title={"Products"} url={URLS.PRODUCTS} />}
            {props.hasAdminPermission && <NavbarLink title={"RiskX"} url={URLS.PARAMETERS} />}
            {props.hasAdminPermission && <NavbarLink title={"System Admin"} url={URLS.ADMIN_EMAILS} />}
            <NavbarLink title={"States"} url={URLS.STATES} />
            {props.hasAdminPermission && <NavbarLink title={"Trading"} url={URLS.TRADING} />}
          </Nav>

          {displayAgencies()}

          <NavDropdown title={email} id="account-dropdown">
            <NavDropdown.Item eventKey="4.1" onClick={props.logout}>
              Logout
            </NavDropdown.Item>
            <NavDropdown.Item eventKey="reset-link" onClick={() => redirectTo("/auth/reset-password")}>
              Reset Password
            </NavDropdown.Item>
          </NavDropdown>
        </Navbar.Collapse>
      )}
    </Navbar>
  );
};

const mapStateToProps = state => {
  const { isSignedIn, email } = loginInfoSelector(state);
  const roles = state.auth.activeAgencyRoles;
  return {
    hasAdminPermission: isAdmin(state),
    activeAgency: activeAgencySelector(state),
    agencies: agenciesSelector(state),
    isSignedIn,
    email,
    roles,
    userId: state.auth.user_id,
  };
};

export default connect(mapStateToProps, {
  logout,
  selectAgency,
  fetchFunders,
  fetchNotifications,
  cleanLifeSettlementSearchData,
})(AHQNavbar);
